import { c as _c } from "react/compiler-runtime";
import React from "react";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";
import type { FilePreviewProps } from "../reducers/types";
import VideoFallbackPreview from "./VideoFallbackPreview";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faCube } from "@fortawesome/free-solid-svg-icons";

// Dynamically import GLB viewer to avoid SSR issues
const MediaDisplayGLB = dynamic(() => import("../../../../drops/view/item/content/media/MediaDisplayGLB"), {
  ssr: false,
  loading: () => <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-iron-400">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
          <FontAwesomeIcon icon={faCube} className="tw-w-8 tw-h-8 tw-flex-shrink-0" />
          <span>Loading 3D model...</span>
        </div>
      </div>
});

/**
 * File Preview Component
 *
 * Displays a preview of the uploaded file, with specialized handling for images and videos.
 * Includes compatibility checking and fallback for non-playable videos.
 *
 * @param props Component props
 * @returns JSX Element
 */
const FilePreview: React.FC<FilePreviewProps> = t0 => {
  const $ = _c(40);
  const {
    url,
    file,
    onRemove,
    videoCompatibility,
    isCheckingCompatibility
  } = t0;
  let t1;
  if ($[0] !== file?.type || $[1] !== url) {
    t1 = file?.type.startsWith("video/") || url.startsWith("data:video/");
    $[0] = file?.type;
    $[1] = url;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isVideo = t1;
  let t2;
  if ($[3] !== file?.name || $[4] !== file?.type) {
    t2 = file?.type === "model/gltf-binary" || file?.type === "model/gltf+json" || file?.name.toLowerCase().endsWith(".glb") || file?.name.toLowerCase().endsWith(".gltf");
    $[3] = file?.name;
    $[4] = file?.type;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isGLB = t2;
  const canPlayVideo = !isVideo || (videoCompatibility?.canPlay ?? true);
  let t3;
  if ($[6] !== url) {
    t3 = () => <img src={url} alt="Artwork preview" className="tw-max-w-full tw-max-h-full tw-object-contain tw-shadow-lg tw-absolute" />;
    $[6] = url;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const renderImagePreview = t3;
  let t4;
  if ($[8] !== url) {
    t4 = () => <video src={url} className="tw-max-w-full tw-max-h-full tw-object-contain tw-shadow-lg" controls={true} onError={_temp} />;
    $[8] = url;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const renderVideoPreview = t4;
  let t5;
  if ($[10] !== isVideo || $[11] !== renderImagePreview || $[12] !== renderVideoPreview || $[13] !== url) {
    t5 = () => {
      if (url.startsWith("data:image/")) {
        return renderImagePreview();
      }
      if (isVideo) {
        return renderVideoPreview();
      }
      return renderImagePreview();
    };
    $[10] = isVideo;
    $[11] = renderImagePreview;
    $[12] = renderVideoPreview;
    $[13] = url;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const renderStandardPreview = t5;
  let t6;
  if ($[15] !== canPlayVideo || $[16] !== file || $[17] !== isGLB || $[18] !== isVideo || $[19] !== renderStandardPreview || $[20] !== url || $[21] !== videoCompatibility?.errorMessage) {
    t6 = () => {
      if (isGLB) {
        return <div className="tw-w-full tw-h-full tw-min-h-[300px]"><MediaDisplayGLB src={url} /></div>;
      }
      const shouldShowStandardPreview = !isVideo || canPlayVideo;
      if (shouldShowStandardPreview) {
        return <>{renderStandardPreview()}</>;
      }
      if (file) {
        return <VideoFallbackPreview file={file} errorMessage={videoCompatibility?.errorMessage} />;
      }
      return null;
    };
    $[15] = canPlayVideo;
    $[16] = file;
    $[17] = isGLB;
    $[18] = isVideo;
    $[19] = renderStandardPreview;
    $[20] = url;
    $[21] = videoCompatibility?.errorMessage;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const renderMediaContent = t6;
  let t7;
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0
    };
    t8 = {
      opacity: 1
    };
    $[23] = t7;
    $[24] = t8;
  } else {
    t7 = $[23];
    t8 = $[24];
  }
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="tw-absolute tw-inset-0 tw-opacity-5"><div className="tw-grid tw-grid-cols-8 tw-h-full">{Array(64).fill(0).map(_temp2)}</div></div>;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== isCheckingCompatibility || $[27] !== isVideo) {
    t10 = isCheckingCompatibility && isVideo && <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-iron-950/70 tw-z-10"><div className="tw-flex tw-flex-col tw-items-center tw-gap-3"><div className="tw-h-10 tw-w-10 tw-border-t-2 tw-border-b-2 tw-border-primary-500 tw-rounded-full tw-animate-spin" /><span className="tw-text-iron-300">Checking video compatibility...</span></div></div>;
    $[26] = isCheckingCompatibility;
    $[27] = isVideo;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== renderMediaContent) {
    t11 = renderMediaContent();
    $[29] = renderMediaContent;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11) {
    t12 = <div className="tw-relative tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-p-4 tw-overflow-hidden">{t11}</div>;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <FontAwesomeIcon icon={faArrowsRotate} className="tw-w-3.5 tw-h-3.5 tw-flex-shrink-0" />;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== onRemove) {
    t14 = <div className="tw-absolute tw-top-3 tw-right-3 tw-z-30 tw-flex tw-gap-2"><button onClick={onRemove} className="tw-border tw-border-solid tw-border-iron-800 hover:tw-ring-iron-650 hover:tw-bg-iron-700 hover:tw-border-iron-700 tw-ring-1 tw-ring-iron-700 tw-rounded-lg tw-bg-iron-800 tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-iron-300 tw-flex tw-items-center tw-justify-center tw-gap-x-2 tw-shadow-sm focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-iron-700 tw-transition tw-duration-300 tw-ease-out" aria-label="Replace uploaded file" tabIndex={0} data-testid="artwork-replace-button">{t13}Replace</button></div>;
    $[34] = onRemove;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t10 || $[37] !== t12 || $[38] !== t14) {
    t15 = <motion.div initial={t7} animate={t8} className="tw-relative tw-w-full tw-h-full tw-bg-iron-900 tw-flex tw-items-center tw-justify-center">{t9}{t10}{t12}{t14}</motion.div>;
    $[36] = t10;
    $[37] = t12;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
};
export default FilePreview;
function _temp(e) {
  console.error("Video playback error:", e);
}
function _temp2(_, i) {
  return <div key={i} className={`tw-border-[0.5px] ${i % 2 === 0 ? "tw-bg-iron-400/10" : "tw-bg-iron-600/10"}`} />;
}